import {
  OrdenServicio,
  OrdenServicioFiltro,
  OrdenServicioPageResponse,
  InformacionTareaFiltro,
  InformacionTareaPageResponse,
  OrdenServicioConfiguracion,
  InformacionTareasConfiguracion,
  OrdenServicioDetalleProgramar,
  ProgramacionServicio,
  TareaBeneficiario,
  TareaBeneficiarioPage,
  AsesorSugerido
} from '../../model/orden-servicio/orden-servicio';

/**
 * Puerto Abstracto: OrdenServicioRepository
 *
 * Define el contrato para gestión de órdenes de servicio (Programar Servicios).
 * Migrado desde: FilterProgramarServiciosViewImpl.java
 *
 * Arquitectura Hexagonal:
 * - Este puerto está en el dominio
 * - HttpOrdenServicioRepository (adaptador) lo implementa en infraestructura
 *
 * Otros dominios tienen sus propios repositories:
 * - AseguradoraRepository - /api/servicios/aseguradoras
 * - CategoriaClienteRepository - /api/servicios/categorias-cliente
 * - ClienteMacrosegRepository - /api/servicios/macrosegmentaciones
 * - ClienteMicrosegRepository - /api/servicios/microsegmentaciones
 * - OficinaRepository - /api/servicios/oficinas
 * - UnidadAdministrativaRepository - /api/servicios/unidades-administrativas
 *
 * @author Sura - Migración GWT a Spring Boot
 */
export abstract class OrdenServicioRepository {
  // === Informacion de Tareas ===

  /**
   * Consulta orientaciones para el modulo Informacion de Tareas.
   */
  abstract consultarInformacionTareas(
    filtro: InformacionTareaFiltro
  ): Promise<InformacionTareaPageResponse>;

  /**
   * Obtiene configuracion funcional para Informacion de Tareas.
   */
  abstract obtenerConfiguracionInformacionTareas(): Promise<InformacionTareasConfiguracion>;

  // === Consulta de Órdenes ===

  /**
   * Consulta órdenes de servicio paginadas según filtros.
   *
   * Campos obligatorios:
   * - Cliente (cdcliente O tipoIdCliente + numeroIdCliente)
   * - Fechas de aprobación (máximo 6 meses de rango)
   */
  abstract consultarOrdenes(
    filtro: OrdenServicioFiltro
  ): Promise<OrdenServicioPageResponse>;

  /**
   * Obtiene una orden específica por su consecutivo.
   */
  abstract obtenerOrdenPorConsecutivo(
    nmconsecutivo: number
  ): Promise<OrdenServicio>;

  // === Bloqueo/Desbloqueo ===

  /**
   * Bloquea una orden para evitar que otros usuarios la programen.
   * Establece SN_BLOQUEADA_USR = 'S' en TPSC_ORDEN_SERVICIO_DETAL.
   */
  abstract bloquearOrden(nmconsecutivo: number): Promise<void>;

  /**
   * Desbloquea una orden previamente bloqueada.
   * Establece SN_BLOQUEADA_USR = 'N' en TPSC_ORDEN_SERVICIO_DETAL.
   */
  abstract desbloquearOrden(nmconsecutivo: number): Promise<void>;

  // === Programar Servicio ===

  /**
   * Obtiene el detalle completo de una orden para el panel de programación.
   *
   * Legacy: ServiceDetailsInterfaceImpl
   */
  abstract obtenerDetalle(
    nmconsecutivo: number
  ): Promise<OrdenServicioDetalleProgramar>;

  /**
   * Consulta las programaciones (citas) de una orden.
   *
   * Legacy: ProgramarServiciosViewImpl.crearSuraTableOrdenes()
   * SP: PCK_PSC_REPORTES.SP_PSC_LISTADO_PROGRAMACIONES
   */
  abstract consultarProgramaciones(
    nmconsecutivo: number
  ): Promise<ProgramacionServicio[]>;

  /**
   * Consulta las tareas pendientes del mismo beneficiario con paginación.
   *
   * Legacy: ProgramarServiciosViewImpl.crearSuraTableOrdenesBeneficiario()
   * SP: PCK_PSC_PROGRAMAR_ORDENES.SP_PSC_LISTAR_OC_BENEFICIARIO
   *
   * @param nmconsecutivoBeneficiario Consecutivo del beneficiario
   * @param nmconsecutivoOrdenActual Orden actual para marcar
   * @param page Número de página (0-based)
   * @param size Tamaño de página
   * @returns Página de tareas con metadatos de paginación
   */
  abstract consultarTareasBeneficiario(
    nmconsecutivoBeneficiario: number,
    nmconsecutivoOrdenActual: number,
    page?: number,
    size?: number
  ): Promise<TareaBeneficiarioPage>;

  /**
   * Obtiene configuración de actas para Ordenes de Servicio.
   */
  abstract obtenerConfiguracionActas(): Promise<OrdenServicioConfiguracion>;

  // === Cumplimiento ===

  /**
   * Actualiza los datos de contacto de un cumplimiento.
   *
   * @param nmconsecutivoCumplimiento Consecutivo del cumplimiento
   * @param nombreContacto Nuevo nombre del contacto
   * @param emailContacto Nuevo email del contacto
   * @returns Promise que resuelve a true si se actualizó correctamente
   */
  abstract actualizarContactoCumplimiento(
    nmconsecutivoCumplimiento: number,
    nombreContacto: string,
    emailContacto: string
  ): Promise<boolean>;

  /**
   * Ejecuta la modificación HU3 de cumplimiento reutilizando el contrato
   * transaccional del dominio de Cumplimientos.
   *
   * Nota: el request/response se manejan como passthrough para no duplicar
   * modelos ni lógica en el dominio de Orden Servicio.
   */
  abstract modificarCumplimientoInformacionTareas(
    request: Record<string, unknown>
  ): Promise<Record<string, unknown>>;

  // === Asociar Acta ===

  /**
   * Asocia un acta a un cumplimiento mediante el servicio externo.
   *
   * Legacy: ProgramarServiciosActivity.asociarActas(), AdminCumplimientoHandler.asociarActasCumplimientos()
   * Servicio: POST {URL}/arlserviciosfc/api/programacion/asociarActa
   *
   * @param idCumplimiento ID del cumplimiento (CDCONSECUTIVO_CUMPLIMIENTO de TGFC_CUMPLIM_ACTA)
   * @param consecutivoActa Consecutivo del acta (CDCONSECUTIVO_ACTA de TGFC_CUMPLIM_ACTA)
   * @returns Objeto con resultado de la operación
   */
  abstract asociarActa(
    idCumplimiento: string,
    consecutivoActa: string
  ): Promise<{ exito: boolean; mensaje: string }>;

  // === Asesores Sugeridos ===

  /**
   * Consulta los asesores sugeridos para programar una cita.
   * Ordenados por prioridad: SUGERIDO > ATENDIÓ > OTROS.
   *
   * Lógica de retry: si no se encuentran asesores y incluirOtros=N,
   * se reintenta automáticamente con incluirOtros=S.
   *
   * Legacy: ScheduleAdvisorActivity.searchSuggestedAdvisors()
   * SP: PCK_PSC_PROGRAMAR_ORDENES.SP_GET_POSIBLES_ASESORES
   *
   * @param nmconsecutivoTarea Consecutivo del detalle de orden
   * @param incluirSugerido Incluir asesor pre-asignado (S/N, default S)
   * @param incluirAtendieron Incluir asesores que ya atendieron (S/N, default S)
   * @param incluirOtros Incluir otros asesores disponibles (S/N, default N)
   * @returns Lista de asesores sugeridos ordenados por prioridad
   */
  abstract consultarAsesoresSugeridos(
    nmconsecutivoTarea: number,
    incluirSugerido?: string,
    incluirAtendieron?: string,
    incluirOtros?: string
  ): Promise<AsesorSugerido[]>;
}
